import { ref, computed, onMounted, onBeforeUnmount } from "vue";
import { auth, db, onAuthStateChanged } from "@/firebase";
import { doc, getDoc, onSnapshot } from "firebase/firestore";
import { logDebug, logInfo, logError, logWarn } from "@/utils/secureLogger";
import {
  canStudentAccessFeatures,
  getStatusMessage,
  type StudentStatus,
} from "@/services/studentStatusService";

/**
 * Vue composable for managing student status and access permissions
 * Provides reactive status state and real-time updates from Firestore
 */
export function useStudentStatus() {
  const status = ref<StudentStatus | null>(null);
  const isLoading = ref(true);
  const error = ref<string | null>(null);
  let unsubscribe: (() => void) | null = null;

  // Computed properties for access control
  const canAccessFeatures = computed(() => {
    if (!status.value) return false;
    return canStudentAccessFeatures(status.value);
  });

  const canBookSessions = computed(() => canAccessFeatures.value);
  const canMessage = computed(() => canAccessFeatures.value);

  const statusMessage = computed(() => {
    if (!status.value) return "";
    return getStatusMessage(status.value);
  });

  const isPending = computed(() => status.value === "pending");
  const isActive = computed(() => status.value === "active" || status.value === "Active");
  const isRejected = computed(() => status.value === "rejected");

  /**
   * Fetch student status from Firestore
   */
  const fetchStatus = async (userId?: string): Promise<void> => {
    try {
      isLoading.value = true;
      error.value = null;

      // Get current user if userId not provided
      const currentUser = userId ? { uid: userId } : auth.currentUser;
      if (!currentUser) {
        throw new Error("No authenticated user found");
      }

      logDebug("Fetching student status", { userId: currentUser.uid });

      // Fetch user document from Firestore
      const userDoc = await getDoc(doc(db, "users", currentUser.uid));

      if (!userDoc.exists()) {
        throw new Error("User document not found");
      }

      const userData = userDoc.data();
      // Check applicationStatus first, fallback to status for backward compatibility
      const userStatus = userData.applicationStatus || userData.status || "pending";

      logDebug("Raw user data from Firestore", {
        userId: currentUser.uid,
        applicationStatus: userData.applicationStatus,
        status: userData.status,
        resolvedStatus: userStatus,
        fullUserData: userData,
      });

      // Validate status
      if (
        ![
          "pending",
          "active",
          "Active",
          "rejected",
          "document_issue",
          "approved",
          "resubmitted_under_review",
        ].includes(userStatus)
      ) {
        logWarn("Invalid status value, defaulting to pending", { status: userStatus });
        status.value = "pending";
      } else {
        status.value = userStatus as StudentStatus;
      }

      logInfo("Student status fetched", {
        userId: currentUser.uid,
        status: status.value,
      });

      // Log access check result
      const accessGranted = canStudentAccessFeatures(status.value);
      console.log("🔐 Access Check Result:", {
        status: status.value,
        canAccess: accessGranted,
        allowedStatuses: ["approved", "active", "Active", "verified"],
      });
    } catch (err) {
      logError("Error fetching student status", err);
      error.value = err instanceof Error ? err.message : "Failed to fetch status";
      status.value = "pending"; // Default to pending on error
    } finally {
      isLoading.value = false;
    }
  };

  /**
   * Set up real-time listener for status changes
   */
  const setupStatusListener = (userId?: string): void => {
    try {
      // Get current user if userId not provided
      const currentUser = userId ? { uid: userId } : auth.currentUser;
      if (!currentUser) {
        logError("Cannot setup listener: no authenticated user");
        return;
      }

      logDebug("Setting up status listener", { userId: currentUser.uid });

      // Set up Firestore real-time listener
      unsubscribe = onSnapshot(
        doc(db, "users", currentUser.uid),
        (docSnapshot) => {
          if (docSnapshot.exists()) {
            const userData = docSnapshot.data();
            // Check applicationStatus first, fallback to status for backward compatibility
            const userStatus = userData.applicationStatus || userData.status || "pending";

            logDebug("Real-time listener - Raw user data from Firestore", {
              userId: currentUser.uid,
              applicationStatus: userData.applicationStatus,
              status: userData.status,
              resolvedStatus: userStatus,
              fullUserData: userData,
            });

            // Validate status
            if (
              ![
                "pending",
                "active",
                "Active",
                "rejected",
                "document_issue",
                "approved",
                "resubmitted_under_review",
              ].includes(userStatus)
            ) {
              logWarn("Invalid status value in real-time update", { status: userStatus });
              status.value = "pending";
            } else {
              const oldStatus = status.value;
              status.value = userStatus as StudentStatus;

              // Log status change
              if (oldStatus !== status.value) {
                logInfo("Student status updated via real-time listener", {
                  userId: currentUser.uid,
                  oldStatus,
                  newStatus: status.value,
                });
              }
            }
          } else {
            logWarn("User document does not exist in real-time listener");
            status.value = "pending";
          }
          isLoading.value = false;
        },
        (err) => {
          logError("Error in status listener", err);
          error.value = "Failed to listen for status updates";
          isLoading.value = false;
        }
      );
    } catch (err) {
      logError("Error setting up status listener", err);
      error.value = "Failed to setup status listener";
    }
  };

  /**
   * Clean up the real-time listener
   */
  const cleanup = (): void => {
    if (unsubscribe) {
      logDebug("Cleaning up status listener");
      unsubscribe();
      unsubscribe = null;
    }
  };

  // Set up listener on mount, but wait for authentication
  onMounted(() => {
    // Listen for auth state changes and set up listener when user is authenticated
    const unsubscribeAuth = onAuthStateChanged(auth, (user) => {
      if (user) {
        logDebug("User authenticated, setting up status listener", { userId: user.uid });
        setupStatusListener();
      } else {
        logDebug("User not authenticated, cleaning up status listener");
        cleanup();
        status.value = null;
        isLoading.value = false;
      }
    });

    // Clean up auth listener on unmount
    onBeforeUnmount(() => {
      unsubscribeAuth();
      cleanup();
    });
  });

  return {
    // State
    status,
    isLoading,
    error,

    // Computed properties
    canAccessFeatures,
    canBookSessions,
    canMessage,
    statusMessage,
    isPending,
    isActive,
    isRejected,

    // Methods
    fetchStatus,
    setupStatusListener,
    cleanup,
  };
}
